<template>
	<div
		class="language-sheet"
		:class="{ 'is-open': isLanguage, 'is-pc': isPc }"
		v-show="isLanguage"
		@click.stop="closeModal"
	>
		<div class="sheet-panel" @click.stop>
			<div class="sheet-handle" v-if="!isPc" aria-hidden="true"></div>
			<div class="sheet-title">{{ $t('account.changeLanguage') }}</div>
			<div class="lang-list">
				<div
					class="lang-item"
					:class="{ active: langIndex === index }"
					v-for="(item, index) in langList"
					:key="item.value"
					@click="langChange(index)"
				>
					<span class="lang-name">{{ item.name }}</span>
					<svg
						v-if="langIndex === index"
						class="lang-check"
						width="18"
						height="18"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M4.5 10.5L8.2 14.2L15.5 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
			</div>
			<div class="sheet-cancel" @click="closeModal">{{ $t('account.cancel') }}</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
	name: 'Languages',
	props: {
		isPc: {
			type: Boolean,
			default: false
		},
		isLanguage: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		...mapGetters(['langList', 'langIndex'])
	},
	watch: {
		isLanguage(val) {
			if (val) {
				document.body.classList.add('language-sheet-open')
			} else {
				document.body.classList.remove('language-sheet-open')
			}
		}
	},
	beforeDestroy() {
		document.body.classList.remove('language-sheet-open')
	},
	methods: {
		langChange(index) {
			const lang = this.langList[index].value
			this.closeModal()
			this.$store.commit('SET_LANG', lang)
			const contentLanguage = lang === 'zh' ? 'zh-CN' : (lang === 'hk' ? 'zh-HK' : lang)
			this.$store.dispatch('listContents', contentLanguage)
		},
		closeModal() {
			this.$emit('close')
		}
	}
}
</script>

<style lang="scss">
.language-sheet {
	position: fixed;
	inset: 0;
	z-index: 100;
	background: var(--ld-overlay, rgba(17, 17, 17, 0.36));
	display: flex;
	align-items: flex-end;
	justify-content: center;
	box-sizing: border-box;
}

.language-sheet.is-pc {
	align-items: center;
	padding: 24px;
}

.language-sheet .sheet-panel {
	width: 100%;
	max-width: 520px;
	background: var(--ld-bg-card, #fff);
	color: var(--ld-text, #111);
	border-radius: 0.2rem 0.2rem 0 0;
	padding: 0.1rem 0.16rem calc(0.16rem + env(safe-area-inset-bottom, 0px));
	box-sizing: border-box;
	max-height: 78vh;
	display: flex;
	flex-direction: column;
	animation: language-sheet-up 0.22s ease-out;
}

.language-sheet.is-pc .sheet-panel {
	width: 400px;
	max-width: 92vw;
	border-radius: 16px;
	padding: 20px 16px 16px;
	max-height: 70vh;
	animation: language-center-in 0.2s ease-out;
	box-shadow: var(--ld-shadow, 0 16px 48px rgba(0, 0, 0, 0.12));
}

.language-sheet .sheet-handle {
	width: 0.36rem;
	height: 0.04rem;
	border-radius: 999px;
	background: var(--ld-handle, #d8d8d8);
	margin: 0.06rem auto 0.12rem;
	flex-shrink: 0;
}

.language-sheet .sheet-title {
	font-size: 0.16rem;
	font-weight: 600;
	text-align: center;
	padding: 0.04rem 0.12rem 0.14rem;
	color: var(--ld-text, #111);
	flex-shrink: 0;
}

.language-sheet.is-pc .sheet-title {
	font-size: 18px;
	padding: 0 8px 14px;
}

.language-sheet .lang-list {
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding: 0 0.04rem;
}

.language-sheet.is-pc .lang-list {
	padding: 0 4px;
}

.language-sheet .lang-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 0.48rem;
	padding: 0.12rem 0.14rem;
	border-radius: 0.12rem;
	font-size: 0.16rem;
	line-height: 1.2;
	color: var(--ld-text, #111);
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;
}

.language-sheet.is-pc .lang-item {
	min-height: 44px;
	padding: 10px 14px;
	font-size: 15px;
	border-radius: 10px;
}

.language-sheet .lang-item:active {
	background: var(--ld-bg-active, #f2f2f2);
}

.language-sheet .lang-item.active {
	background: var(--ld-bg-active, #f2f2f2);
	font-weight: 600;
}

.language-sheet .lang-check {
	color: var(--ld-text, #111);
	flex-shrink: 0;
}

.language-sheet .sheet-cancel {
	margin-top: 0.1rem;
	min-height: 0.48rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 0.12rem;
	background: var(--ld-bg-muted, #f5f5f5);
	color: var(--ld-text, #333);
	font-size: 0.15rem;
	font-weight: 500;
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;
}

.language-sheet.is-pc .sheet-cancel {
	min-height: 44px;
	font-size: 14px;
	border-radius: 10px;
	margin-top: 12px;
}

.language-sheet .sheet-cancel:active {
	background: var(--ld-bg-active, #ececec);
}

@keyframes language-sheet-up {
	from {
		transform: translateY(100%);
	}
	to {
		transform: translateY(0);
	}
}

@keyframes language-center-in {
	from {
		opacity: 0;
		transform: scale(0.96) translateY(8px);
	}
	to {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
}
</style>
